import { useState, useEffect, useCallback } from 'react';
import { SelfEntitlementStatus } from '../types';
import {
  fetchAndCacheSelfEntitlementStatus,
  getCachedSelfEntitlementStatus,
} from '../utils/selfEntitlementService';

interface UseSelfEntitlementReturn {
  status: SelfEntitlementStatus | null;
  loading: boolean;
  error: Error | null;
  refreshStatus: () => Promise<void>;
}

/**
 * useSelfEntitlement Hook
 * 
 * Retrieves the user's self-entitlement status.
 * Backend extracts accountId from JWT cookie.
 * 
 * @param isLoggedIn - Whether the user is currently logged in
 * @returns Object with status, loading, error states and refresh function
 */
export const useSelfEntitlement = (
  isLoggedIn: boolean
): UseSelfEntitlementReturn => {
  const [status, setStatus] = useState<SelfEntitlementStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch status from API or cache
   * forceRefresh allows components to manually refresh if needed
   */
  const fetchStatus = useCallback(async (forceRefresh = false) => {
    if (!isLoggedIn) {
      setStatus(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Check cache first
      if (!forceRefresh) {
        const cached = getCachedSelfEntitlementStatus();
        if (cached) {
          setStatus(cached);
          setLoading(false);
          return;
        }
      }

      // Fetch from API (cache was empty or force refresh requested)
      const fetched = await fetchAndCacheSelfEntitlementStatus();
      if (fetched) {
        setStatus(fetched);
      } else {
        setError(new Error('Failed to fetch self-entitlement status'));
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  /**
   * Public refresh function - forces API call and cache refresh
   */
  const refreshStatus = useCallback(async () => {
    await fetchStatus(true);
  }, [fetchStatus]);

  /**
   * Attempt to load from cache on mount
   * If cache is empty and user is logged in, fetch from API
   */
  useEffect(() => {
    if (isLoggedIn) {
      // Try cache first
      const cached = getCachedSelfEntitlementStatus();
      if (cached) {
        setStatus(cached);
      } else {
        // Cache empty, fetch from API
        fetchStatus();
      }
    } else {
      setStatus(null);
    }
  }, [isLoggedIn, fetchStatus]);

  return {
    status,
    loading,
    error,
    refreshStatus,
  };
};